import { useEffect, useRef } from 'react';

import { captureException } from '@sentry/nextjs';
import { useDispatch } from 'react-redux';
import { useAccount, useConnect, useConnectors } from 'wagmi';

import { isLedgerConnectorId, isLedgerLiveBrowser } from '@/config/ledgerLive';
import { updateWalletData } from '@/store/slices/walletSlice';

/**
 * Connects automatically inside Ledger's DApp Browser, where the wallet is already
 * unlocked and a connect prompt is noise.
 *
 * Returning users need nothing from this hook: Ledger reports the selected account
 * through `eth_accounts`, so wagmi's mount reconnect authorises the Ledger connector on
 * its own. This covers the first visit, where the account has not been granted yet and
 * has to be requested.
 */
const useAutoConnect = () => {
  const connectors = useConnectors();
  const { connect } = useConnect({
    mutation: {
      // This only reproduces inside Ledger's DApp Browser, where attaching a debugger
      // is rarely an option, so a failed auto-connect has to be reported rather than
      // swallowed.
      onError: (error) => captureException(error),
    },
  });
  const { status } = useAccount();
  const dispatch = useDispatch();

  // One attempt per page load, so an explicit disconnect is not undone.
  const hasAttempted = useRef(false);

  useEffect(() => {
    // `wallet.isLoading` is really "is auto-connect still in flight": the header shows
    // a spinner and the stake CTA stays disabled while it is true, so it has to be
    // released on every path out of here.
    const stopLoading = () => dispatch(updateWalletData({ isLoading: false }));

    if (hasAttempted.current) return;

    // wagmi is still restoring a session. Leave the spinner up rather than flashing
    // "Connect wallet"; this re-runs when the status settles.
    if (status === 'connecting' || status === 'reconnecting') return;

    if (status !== 'disconnected' || !isLedgerLiveBrowser()) {
      stopLoading();
      return;
    }

    // Either Ledger connector will do -- the one registered in `@/config/ledgerLive`,
    // or the one wagmi discovers over EIP-6963. `useConnectors()` is reactive, so this
    // re-runs if discovery adds the latter.
    const connector = connectors.find((c) => isLedgerConnectorId(c.id));
    if (!connector) {
      stopLoading();
      return;
    }

    hasAttempted.current = true;
    connect({ connector }, { onSettled: stopLoading });
  }, [connect, connectors, dispatch, status]);
};

export default useAutoConnect;
